// Blocked-access page (User Story 3). Shown when a contact doesn't pass the
// access policy. This is a concierge handoff, not a rejection: it renders the
// admin-configured copy (heading + body, which includes any sales contact) in a
// calm, branded layout and offers an escape hatch to try a different email.
// Copy comes from the app-wide settings slice (populated from /api/public-config
// on mount, with built-in defaults as the initial paint).
import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Container, Paper, Typography, Button,
} from '@mui/material';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { selectSettings } from '../store/settings-slice.js';
import { resetContact } from '../store/contact-slice.js';
import ThemeModeToggle from '../components/ThemeModeToggle.jsx';
import SanbornLogo from '../components/SanbornLogo.jsx';

const BlockedPage = () => {
  const settings = useSelector(selectSettings);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const headingRef = useRef(null);

  useEffect(() => { headingRef.current?.focus(); }, []);

  const handleDifferentEmail = () => {
    dispatch(resetContact());
    navigate('/', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 12, right: 16 }}><ThemeModeToggle /></Box>
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ color: 'text.primary', mb: 3, textAlign: 'center' }}>
          <SanbornLogo height={72} href="https://sanborn.com/" />
        </Box>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            textAlign: 'center',
            border: 1,
            borderColor: 'divider',
            borderTop: 3,
            borderTopColor: 'primary.main',
          }}
        >
          <SupportAgentOutlinedIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
          <Typography
            ref={headingRef}
            tabIndex={-1}
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 700, outline: 'none' }}
          >
            {settings.blocked_page_title}
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 3 }}>
            {settings.blocked_page_body}
          </Typography>
          <Button variant="outlined" onClick={handleDifferentEmail}>
            Try a different email
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default BlockedPage;
